import React, { forwardRef, useEffect, useRef, useState } from 'react';
import './select.css';
import { clsx } from 'clsx';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { SelectChevronIcon } from 'src/assets/icons/SelectChevronIcon';

// An option in the custom selects; they match options by label.
export interface SelectOption {
  label: string;
  value?: unknown;
  icon?: React.ReactNode;
  disabled?: boolean;
  toolTipDescription?: React.ReactNode;
  [key: string]: unknown;
}

interface CustomSelectProps {
  // Shown when nothing is selected.
  label?: string;
  readonly?: boolean;
  options?: SelectOption[];
  onChange?: (option: SelectOption) => void;
  align?: string;
  // Matched to an option by label.
  value?: { label?: string } | null;
  dropdownMenuClassNames?: string;
  // Shown instead of the selected option's label.
  customLabel?: string;
  className?: string;
}

export const CustomSelect = forwardRef<HTMLDivElement, CustomSelectProps>(
  (
    {
      label = 'Please Select',
      readonly = false,
      options = [],
      onChange = () => {},
      align,
      value,
      dropdownMenuClassNames = '',
      customLabel,
      className = '',
    },
    ref,
  ) => {
    const [showMenu, setShowMenu] = useState(false);
    const [selectedValue, setSelectedValue] = useState(options[0]);
    const inputRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      setSelectedValue(options?.find((option) => option?.label === value?.label));
    }, [value]);

    useEffect(() => {
      const handler = (e: MouseEvent) => {
        const target = e.target as Node;

        if (
          inputRef.current &&
          !inputRef.current.contains(target) &&
          menuRef.current &&
          !menuRef.current.contains(target)
        ) {
          setShowMenu(false);
        }
      };

      globalThis.addEventListener('click', handler);
      return () => {
        globalThis.removeEventListener('click', handler);
      };
    }, []);

    const handleInputClick = () => {
      setShowMenu((prevState) => !prevState);
    };

    const onItemClick = (option: SelectOption) => {
      setSelectedValue(option);
      onChange(option);
      setShowMenu((prevState) => !prevState);
    };

    const isSelected = (option: SelectOption) => {
      if (!selectedValue) {
        return false;
      }

      return selectedValue.label === option.label;
    };

    return (
      <div
        ref={ref}
        className={clsx('custom--dropdown-container', readonly ? 'cursor-auto' : 'cursor-pointer')}
      >
        <button
          type="button"
          ref={inputRef}
          onClick={handleInputClick}
          className={clsx(
            showMenu ? 'rounded-t-[5px]' : 'rounded-[5px]',
            'dropdown-input',
            readonly && 'pointer-events-none opacity-70 cursor-auto',
            className,
            'w-full text-left',
          )}
        >
          <div
            title={selectedValue?.label}
            className={`flex items-center gap-[7px] dropdown-selected-value ${!selectedValue || selectedValue.length === 0 ? 'placeholder' : ''}`}
          >
            {selectedValue ? (
              <>
                {selectedValue.icon}
                <span className="text-start hyphens-auto">
                  {customLabel || selectedValue.label}
                </span>
              </>
            ) : (
              label
            )}
          </div>
          <div className="dropdown-tools">
            <div className="dropdown-tool">
              <SelectChevronIcon isOpen={showMenu} />
            </div>
          </div>
        </button>

        {showMenu && (
          <div
            className={clsx(
              dropdownMenuClassNames,
              `dropdown-menu relative alignment--${align || 'auto'} w-full max-h-80`,
            )}
            ref={menuRef}
          >
            {options?.map((option, index) => (
              <TooltipProvider key={`${option?.label}-${index}`}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      disabled={option?.disabled ?? false}
                      onClick={() => onItemClick(option)}
                      className={`flex items-center gap-[7px] w-full dropdown-item ${isSelected(option) && 'selected'}`}
                    >
                      {option?.icon}
                      <span className="text-start hyphens-auto">{option?.label}</span>
                    </button>
                  </TooltipTrigger>
                  {option?.toolTipDescription && (
                    <TooltipContent className="max-w-2xl">
                      {option.toolTipDescription}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        )}
      </div>
    );
  },
);
